import { useEffect, useMemo, useRef, useState } from 'react';
import type { ReviewAsset } from '../../../shared/types';
import {
  nextUploadIds,
  type UploadScheduleItem,
  type UploadScheduleStatus,
} from '../../../shared/uploadScheduler';
import { adminApi, ApiError, API_FALLBACK_ERROR_MESSAGE } from '../../lib/api';
import { primaryButtonClass, secondaryButtonClass } from '../admin/AdminUi';
import { UploadDropzone } from './UploadDropzone';
import {
  UploadProgress,
  type UploadItemState,
  type UploadProjection,
} from './UploadProgress';

interface UploadErrorBody {
  error?: string;
  used_bytes?: number;
  declared_bytes?: number;
  projected_bytes?: number;
  ceiling_bytes?: number;
}

interface PreparedUpload {
  url: string;
  key: string;
  expiresAt: number;
  requiredHeaders: Record<string, string>;
}

interface QueueItem {
  id: string;
  asset: ReviewAsset;
  file: File;
  state: UploadItemState;
  progress: number;
  projection?: UploadProjection;
  prepared?: PreparedUpload;
  error?: string;
}

interface ScheduledUpload extends UploadScheduleItem {
  item: QueueItem;
  attempt: number;
}

const MAX_CONCURRENT_UPLOADS = 3;

export interface FilePairing {
  file: File;
  assetId: string;
  /** False when only the leftover ordering placed this file, so the row asks to be checked. */
  matched: boolean;
}

function comparableText(value: string): string {
  return value.toLowerCase().replace(/\.[^.]+$/, '').replace(/[^a-z0-9]+/g, '');
}

/**
 * Guesses which interview each file belongs to by name, keeping the picker's
 * order only as the last resort. Position alone used to decide this silently,
 * so a differently sorted selection attached the wrong video to the wrong
 * interview with both uploads succeeding.
 */
export function pairFilesToAssets(
  files: readonly File[],
  assets: readonly ReviewAsset[],
): FilePairing[] {
  const pairings = new Array<FilePairing | null>(files.length).fill(null);
  const takenAssetIds = new Set<string>();

  const claim = (fileIndex: number, asset: ReviewAsset, matched: boolean) => {
    pairings[fileIndex] = { file: files[fileIndex]!, assetId: asset.id, matched };
    takenAssetIds.add(asset.id);
  };

  // Exact name matches first, so one ambiguous file can never displace a certain one.
  for (const exactPass of [true, false]) {
    files.forEach((file, fileIndex) => {
      if (pairings[fileIndex]) return;
      const name = comparableText(file.name);
      if (!name) return;
      const matches = assets.filter((asset) => {
        if (takenAssetIds.has(asset.id)) return false;
        const title = comparableText(asset.title);
        if (!title) return false;
        return exactPass ? title === name : name.includes(title) || title.includes(name);
      });
      // "Interview 1" also prefixes "Interview 10", so a fuzzy tie is left for
      // the leftover pass to place and for the confirm step to flag.
      if (matches.length === 1) claim(fileIndex, matches[0]!, true);
    });
  }

  const leftoverAssets = assets.filter((asset) => !takenAssetIds.has(asset.id));
  files.forEach((_file, fileIndex) => {
    if (pairings[fileIndex]) return;
    const asset = leftoverAssets.shift();
    if (asset) claim(fileIndex, asset, false);
  });

  return pairings.filter((pairing): pairing is FilePairing => pairing !== null);
}

/** Two files aimed at one interview would overwrite each other, so the confirm step blocks it. */
export function duplicatePairedAssetIds(pairings: readonly FilePairing[]): string[] {
  const seen = new Set<string>();
  const duplicates = new Set<string>();
  for (const { assetId } of pairings) {
    if (seen.has(assetId)) duplicates.add(assetId);
    seen.add(assetId);
  }
  return [...duplicates];
}

export function shouldReconcileCompletedUpload({
  requestGeneration,
  currentGeneration,
  requestAttempt,
  entryStatus,
  entryAttempt,
}: {
  requestGeneration: number;
  currentGeneration: number;
  requestAttempt: number;
  entryStatus: UploadScheduleStatus | undefined;
  entryAttempt: number | undefined;
}): boolean {
  if (requestGeneration !== currentGeneration) return false;
  return (
    (entryStatus === 'running' && entryAttempt === requestAttempt) ||
    entryStatus === 'cancelled'
  );
}

export interface AssetUploaderProps {
  asset: ReviewAsset | readonly ReviewAsset[];
  onComplete: (asset: ReviewAsset, file: File) => void;
  onQueueComplete?: () => void;
  disabled?: boolean;
}

function errorProjection(value: unknown): UploadProjection | undefined {
  if (!value || typeof value !== 'object') return undefined;
  const body = value as UploadErrorBody;
  if (
    typeof body.used_bytes !== 'number' ||
    typeof body.declared_bytes !== 'number' ||
    typeof body.projected_bytes !== 'number' ||
    typeof body.ceiling_bytes !== 'number'
  ) {
    return undefined;
  }
  return {
    usedBytes: body.used_bytes,
    declaredBytes: body.declared_bytes,
    projectedBytes: body.projected_bytes,
    ceilingBytes: body.ceiling_bytes,
  };
}

function putFile(
  prepared: PreparedUpload,
  file: File,
  onProgress: (progress: number) => void,
  onCreated: (xhr: XMLHttpRequest) => void,
): Promise<string> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    onCreated(xhr);
    xhr.open('PUT', prepared.url);
    for (const [name, value] of Object.entries(prepared.requiredHeaders)) xhr.setRequestHeader(name, value);
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) onProgress((event.loaded / event.total) * 100);
    };
    xhr.onerror = () => reject(new Error('The upload was interrupted. Check your connection, then try again.'));
    xhr.onabort = () => reject(new DOMException('Upload cancelled.', 'AbortError'));
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(xhr.getResponseHeader('ETag') ?? '');
      } else {
        reject(new Error('Storage did not accept the file. Please try again.'));
      }
    };
    xhr.send(file);
  });
}

export function AssetUploader({
  asset,
  onComplete,
  onQueueComplete,
  disabled = false,
}: AssetUploaderProps) {
  const assets = useMemo(() => (Array.isArray(asset) ? [...asset] : [asset as ReviewAsset]), [asset]);
  const [items, setItems] = useState<QueueItem[]>([]);
  const [selectionError, setSelectionError] = useState<string | null>(null);
  const [pairings, setPairings] = useState<FilePairing[] | null>(null);
  const requests = useRef(new Map<string, XMLHttpRequest>());
  const preparations = useRef(new Map<string, AbortController>());
  const cancelledIds = useRef(new Set<string>());
  const scheduledUploads = useRef(new Map<string, ScheduledUpload>());
  const queueGeneration = useRef(0);
  const queueStartedCount = useRef(0);
  const queueCompleteFired = useRef(false);

  useEffect(
    () => () => {
      queueGeneration.current += 1;
      for (const request of requests.current.values()) request.abort();
      for (const controller of preparations.current.values()) controller.abort();
      requests.current.clear();
      preparations.current.clear();
    },
    [],
  );

  const updateItem = (id: string, update: Partial<QueueItem>) => {
    setItems((current) => current.map((item) => (item.id === id ? { ...item, ...update } : item)));
  };

  const isCurrentAttempt = (id: string, generation: number, attempt: number) => {
    const entry = scheduledUploads.current.get(id);
    return (
      generation === queueGeneration.current &&
      entry?.status === 'running' &&
      entry.attempt === attempt
    );
  };

  const updateCurrentItem = (
    id: string,
    generation: number,
    attempt: number,
    update: Partial<QueueItem>,
  ) => {
    if (isCurrentAttempt(id, generation, attempt)) updateItem(id, update);
  };

  const prepare = async (
    item: QueueItem,
    generation: number,
    attempt: number,
  ): Promise<PreparedUpload | null> => {
    updateCurrentItem(item.id, generation, attempt, {
      state: 'checking',
      progress: 0,
      error: undefined,
      prepared: undefined,
    });
    const controller = new AbortController();
    preparations.current.set(item.id, controller);
    try {
      const result = await adminApi.createUploadUrl(
        item.asset.id,
        {
          filename: item.file.name,
          mime_type: 'video/mp4',
          bytes: item.file.size,
        },
        controller.signal,
      );
      if (preparations.current.get(item.id) === controller) preparations.current.delete(item.id);
      const projection: UploadProjection = {
        usedBytes: result.storage.used_bytes,
        declaredBytes: result.storage.declared_bytes,
        projectedBytes: result.storage.projected_bytes,
        ceilingBytes: result.storage.ceiling_bytes,
      };
      const prepared = {
        url: result.upload_url,
        key: result.r2_key,
        expiresAt: result.expires_at,
        requiredHeaders: result.required_headers,
      };
      updateCurrentItem(item.id, generation, attempt, { state: 'ready', projection, prepared });
      return prepared;
    } catch (error) {
      if (preparations.current.get(item.id) === controller) preparations.current.delete(item.id);
      if (error instanceof DOMException && error.name === 'AbortError') {
        updateCurrentItem(item.id, generation, attempt, {
          state: 'cancelled',
          error: undefined,
          prepared: undefined,
        });
        return null;
      }
      const message = error instanceof ApiError && error.message === API_FALLBACK_ERROR_MESSAGE
        ? 'The upload could not be prepared.'
        : error instanceof Error
          ? error.message
          : 'The upload could not be prepared.';
      updateCurrentItem(item.id, generation, attempt, {
        state: 'failed',
        error: message,
        projection: error instanceof ApiError ? errorProjection(error.body) : undefined,
      });
      return null;
    }
  };

  const upload = async (
    item: QueueItem,
    generation: number,
    attempt: number,
    suppliedPrepared?: PreparedUpload,
  ): Promise<Extract<UploadScheduleStatus, 'complete' | 'failed' | 'cancelled'>> => {
    let prepared = suppliedPrepared ?? item.prepared;
    if (!prepared || prepared.expiresAt <= Date.now() + 5_000) {
      const refreshed = await prepare(item, generation, attempt);
      if (!refreshed) return cancelledIds.current.has(item.id) ? 'cancelled' : 'failed';
      prepared = refreshed;
    }

    updateCurrentItem(item.id, generation, attempt, {
      state: 'uploading',
      progress: 0,
      error: undefined,
      prepared,
    });
    let request: XMLHttpRequest | undefined;
    try {
      const etag = await putFile(
        prepared,
        item.file,
        (progress) => updateCurrentItem(item.id, generation, attempt, { progress }),
        (xhr) => {
          request = xhr;
          requests.current.set(item.id, xhr);
        },
      );
      if (requests.current.get(item.id) === request) requests.current.delete(item.id);
      const result = await adminApi.completeUpload(item.asset.id, {
        r2_key: prepared.key,
        bytes: item.file.size,
        etag,
      });
      const entry = scheduledUploads.current.get(item.id);
      if (!shouldReconcileCompletedUpload({
        requestGeneration: generation,
        currentGeneration: queueGeneration.current,
        requestAttempt: attempt,
        entryStatus: entry?.status,
        entryAttempt: entry?.attempt,
      })) return 'cancelled';
      if (entry) entry.status = 'complete';
      updateItem(item.id, { state: 'complete', progress: 100, error: undefined });
      onComplete(result.asset, item.file);
      return 'complete';
    } catch (error) {
      if (requests.current.get(item.id) === request) requests.current.delete(item.id);
      if (error instanceof DOMException && error.name === 'AbortError') {
        updateCurrentItem(item.id, generation, attempt, {
          state: 'cancelled',
          error: undefined,
          prepared: undefined,
        });
        return 'cancelled';
      } else {
        updateCurrentItem(item.id, generation, attempt, {
          state: 'failed',
          error: error instanceof ApiError && error.message === API_FALLBACK_ERROR_MESSAGE
            ? 'The upload could not be prepared.'
            : error instanceof Error
              ? error.message
              : 'The upload stopped. Please try again.',
          prepared: undefined,
        });
        return 'failed';
      }
    }
  };

  const completeQueueIfSettled = (generation: number) => {
    if (generation !== queueGeneration.current || queueCompleteFired.current) return;
    const queueSettled = Array.from(scheduledUploads.current.values()).every(
      (entry) => entry.status !== 'queued' && entry.status !== 'running',
    );
    if (queueSettled && queueStartedCount.current > 0) {
      queueCompleteFired.current = true;
      onQueueComplete?.();
    }
  };

  async function runScheduledUpload(
    entry: ScheduledUpload,
    generation: number,
    attempt: number,
  ) {
    const prepared = await prepare(entry.item, generation, attempt);
    if (
      generation !== queueGeneration.current ||
      entry.status !== 'running' ||
      entry.attempt !== attempt ||
      cancelledIds.current.has(entry.id)
    ) {
      return;
    }

    if (!prepared) {
      entry.status = cancelledIds.current.has(entry.id) ? 'cancelled' : 'failed';
      scheduleUploads(generation);
      return;
    }

    queueStartedCount.current += 1;
    const result = await upload(entry.item, generation, attempt, prepared);
    if (generation !== queueGeneration.current) return;
    if (entry.status === 'running' && entry.attempt === attempt) entry.status = result;
    scheduleUploads(generation);
  }

  function scheduleUploads(generation: number) {
    if (generation !== queueGeneration.current) return;
    const entries = Array.from(scheduledUploads.current.values());
    const idsToStart = nextUploadIds(entries, MAX_CONCURRENT_UPLOADS);
    for (const id of idsToStart) {
      const entry = scheduledUploads.current.get(id);
      if (!entry || entry.status !== 'queued') continue;
      entry.status = 'running';
      entry.attempt += 1;
      void runScheduledUpload(entry, generation, entry.attempt);
    }
    completeQueueIfSettled(generation);
  }

  const selectFiles = (files: File[]) => {
    setSelectionError(null);
    if (files.length > assets.length) {
      setSelectionError(
        assets.length === 1
          ? 'Choose one MP4 for this interview.'
          : `Choose up to ${assets.length} MP4 files, one for each interview.`,
      );
      return;
    }
    if (
      files.some(
        (file) => !file.name.toLowerCase().endsWith('.mp4') || (file.type && file.type !== 'video/mp4'),
      )
    ) {
      setSelectionError('Choose MP4 review proxies only.');
      return;
    }

    const paired = pairFilesToAssets(files, assets);
    // One interview leaves nothing to get wrong, so it uploads straight away.
    if (assets.length === 1) startUploads(paired);
    else setPairings(paired);
  };

  const startUploads = (paired: readonly FilePairing[]) => {
    const assetsById = new Map(assets.map((asset) => [asset.id, asset]));
    const queued = paired.flatMap<QueueItem>(({ file, assetId }) => {
      const asset = assetsById.get(assetId);
      return asset
        ? [{ id: `${asset.id}:${file.name}:${file.lastModified}`, asset, file, state: 'checking', progress: 0 }]
        : [];
    });
    setPairings(null);
    cancelledIds.current.clear();
    queueGeneration.current += 1;
    queueStartedCount.current = 0;
    queueCompleteFired.current = false;
    scheduledUploads.current = new Map(
      queued.map((item) => [item.id, { id: item.id, item, status: 'queued', attempt: 0 }]),
    );
    setItems(queued);
    scheduleUploads(queueGeneration.current);
  };

  const repair = (fileIndex: number, assetId: string) => {
    setPairings((current) => current?.map(
      (pairing, index) => (index === fileIndex ? { ...pairing, assetId, matched: true } : pairing),
    ) ?? null);
  };

  const busy = items.some(
    (item) => item.state === 'checking' || item.state === 'ready' || item.state === 'uploading',
  );
  const duplicateAssetIds = new Set(pairings ? duplicatePairedAssetIds(pairings) : []);
  const unsureCount = pairings?.filter((pairing) => !pairing.matched).length ?? 0;

  return (
    <section aria-label="Media uploads" className="space-y-4">
      <UploadDropzone
        multiple={assets.length > 1}
        disabled={disabled || busy || pairings !== null}
        onFiles={(files) => void selectFiles(files)}
      />

      {selectionError && (
        <p className="rounded-input bg-error-tint px-4 py-3 text-error" role="alert">
          {selectionError}
        </p>
      )}

      {pairings && (
        <div className="rounded-card border border-line bg-card p-4 shadow-card">
          <h3 className="font-semibold text-fg">Check which interview each file belongs to</h3>
          <p className="mt-1 text-sm text-faint">
            {unsureCount === 0
              ? 'Matched by filename. Change any that look wrong before uploading.'
              : `${unsureCount} ${unsureCount === 1 ? 'file' : 'files'} could not be matched by name, so ${unsureCount === 1 ? 'it is' : 'they are'} paired in the order you chose them.`}
          </p>

          <ul className="mt-4 space-y-3">
            {pairings.map((pairing, index) => (
              <li key={`${pairing.file.name}:${pairing.file.lastModified}`} className="flex flex-wrap items-center gap-3">
                <span className="min-w-0 flex-1 truncate text-sm text-fg" title={pairing.file.name}>
                  {pairing.file.name}
                </span>
                <label className="flex items-center gap-2">
                  <span className="sr-only">Interview for {pairing.file.name}</span>
                  <select
                    className="min-h-9 rounded-input border border-line bg-page px-2 py-1 text-sm text-fg"
                    value={pairing.assetId}
                    onChange={(event) => repair(index, event.target.value)}
                  >
                    {assets.map((asset) => (
                      <option key={asset.id} value={asset.id}>{asset.title}</option>
                    ))}
                  </select>
                </label>
                {duplicateAssetIds.has(pairing.assetId) ? (
                  <span className="font-mono text-[0.7rem] uppercase tracking-[0.12em] text-error">Clash</span>
                ) : pairing.matched ? null : (
                  <span className="font-mono text-[0.7rem] uppercase tracking-[0.12em] text-warning">Check</span>
                )}
              </li>
            ))}
          </ul>

          {duplicateAssetIds.size > 0 && (
            <p className="mt-3 rounded-input bg-error-tint px-4 py-3 text-error" role="alert">
              Two files are pointing at the same interview. Give each file its own interview.
            </p>
          )}

          <div className="mt-4 flex flex-wrap justify-end gap-2">
            <button type="button" className={secondaryButtonClass} onClick={() => setPairings(null)}>
              Choose different files
            </button>
            <button
              type="button"
              className={primaryButtonClass}
              disabled={disabled || duplicateAssetIds.size > 0}
              onClick={() => startUploads(pairings)}
            >
              Upload {pairings.length} {pairings.length === 1 ? 'file' : 'files'}
            </button>
          </div>
        </div>
      )}

      <div className="space-y-3" aria-live="polite">
        {items.map((item) => (
          <UploadProgress
            key={item.id}
            filename={item.file.name}
            assetTitle={item.asset.title}
            state={item.state}
            progress={item.progress}
            projection={item.projection}
            error={item.error}
            onCancel={() => {
              cancelledIds.current.add(item.id);
              const entry = scheduledUploads.current.get(item.id);
              if (entry && (entry.status === 'queued' || entry.status === 'running')) {
                entry.status = 'cancelled';
                entry.attempt += 1;
              }
              const preparation = preparations.current.get(item.id);
              if (preparation) {
                preparation.abort();
                preparations.current.delete(item.id);
              }
              const request = requests.current.get(item.id);
              if (request) request.abort();
              updateItem(item.id, { state: 'cancelled', error: undefined, prepared: undefined });
              scheduleUploads(queueGeneration.current);
            }}
            onRetry={() => {
              cancelledIds.current.delete(item.id);
              const entry = scheduledUploads.current.get(item.id);
              if (!entry) return;
              entry.status = 'queued';
              queueCompleteFired.current = false;
              updateItem(item.id, {
                state: 'checking',
                progress: 0,
                error: undefined,
                prepared: undefined,
              });
              scheduleUploads(queueGeneration.current);
            }}
          />
        ))}
      </div>
    </section>
  );
}
